// Bot / otomasyon / sahte-tarayıcı tespiti — A/B/C gruplu karar sistemi.
//
// BAĞLAM: Bu uygulamaya yalnızca normal masaüstü ve mobil tarayıcılardan
// (Chrome, Edge, Firefox, Safari, mobil Chrome/Safari) erişilir. WebView2 /
// Electron / gömülü tarayıcı tabanlı RESMİ bir uygulamamız YOKTUR — dolayısıyla
// gömülü tarayıcıdan gelen her istek %100 şüphelidir ve koşulsuz bloklanır.
//
// ÖNEMLİ: Client tarafı tespit %100 engelleme DEĞİLDİR; UA taklit edilebilir,
// global'ler enjekte edilebilir. Üstelik JS ÇALIŞTIRMAYAN istemciler (curl,
// python-requests, Go-http-client) bu kodun hiçbirini çalıştırmaz — onları
// ancak sunucu (nginx / Cloudflare / backend) Origin-Referer + header
// tutarlılığı kontrolüyle keser. Bu katman, JS ÇALIŞTIRAN ama kendini gerçek
// tarayıcı gibi göstermeye çalışan botları yakalar; yakalanınca main.tsx
// bembeyaz boş sayfa render eder (sessiz; konsola iz bırakmaz).
//
// KARAR MANTIĞI:
//   A) Koşulsuz blok        → tek sinyal yeter
//   B) Tutarsızlık (spoof)  → tek sinyal yeter
//   C) Zayıf sinyaller      → tek başına yetmez, EN AZ İKİSİ birleşince bloklar
//   Emin olunmayan durumda kullanıcı GEÇİRİLİR (yanlış pozitif = beyaz sayfa).

interface GuardResult {
  /** Engelleme tetiklendi mi */
  blocked: boolean;
  /** Tetikleyen sinyal(ler) — teşhis için; konsola yazılmaz */
  reason: string | null;
}

// navigator.userAgentData henüz tüm TS lib sürümlerinde tanımlı değil.
interface UADataBrand {
  brand: string;
  version: string;
}
interface NavigatorUAData {
  brands?: UADataBrand[];
  mobile?: boolean;
  platform?: string;
}

// UA'dan Chromium ana sürümünü çıkar. iOS Chrome (CriOS), Safari, Firefox'ta
// bu token yok → null döner (onlar Chromium sayılmaz).
const chromiumMajorFromUA = (ua: string): number | null => {
  const m = ua.match(/(?:Chrome|Chromium|Edg)\/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};

const getUAData = (nav: Navigator): NavigatorUAData | undefined =>
  (nav as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;

const isMobileClient = (ua: string, uaData: NavigatorUAData | undefined): boolean =>
  uaData?.mobile === true || /Mobile|Android|iPhone|iPad|iPod/i.test(ua);

// ===========================================================================
// A) KOŞULSUZ BLOK — tek sinyal yeter
// ===========================================================================

// A0) Gömülü çalışma ortamı (Electron, NW.js, Tauri, WebView2 host, Android/iOS
//     WebView, iframe). Resmi gömülü uygulamamız olmadığı için hepsi koşulsuz blok.
const sigEmbedded = (ua: string, w: Record<string, any>): string | null => {
  if (/nwjs/i.test(ua)) return 'nwjs-ua';
  if (/QtWebEngine/i.test(ua)) return 'qtwebengine-ua';
  if (w.process?.type || typeof w.require === 'function') return 'electron-node-bridge';
  if (w.__TAURI__ || w.__TAURI_INTERNALS__ || w.__TAURI_METADATA__) return 'tauri-global';
  if (w.chrome?.webview) return 'webview2-host';
  // NOT: window.webkit.messageHandlers ÇIKARILDI — iOS'ta TÜM tarayıcılar
  // (Chrome/CriOS, Edge/EdgiOS, Firefox/FxiOS, çoğu zaman Safari) WKWebView
  // tabanlı olduğu için bu köprüyü enjekte eder; gerçek iOS Chrome'u bile
  // bloklardı (yanlış pozitif). Android JS-interface ve RN WebView köprüsü
  // güvenli kalır; Android in-app WebView'leri ayrıca "; wv)" UA ile yakalanır.
  if (w.Android || w.ReactNativeWebView) return 'js-bridge';
  if (/;\s*wv\)/.test(ua)) return 'android-webview-ua';
  try {
    if (window.self !== window.top) return 'embedded-iframe';
  } catch {
    return 'embedded-iframe-cross-origin';
  }
  return null;
};

// A1) userAgentData.brands listesinde "WebView2".
const sigWebView2Brand = (uaData: NavigatorUAData | undefined): string | null =>
  uaData?.brands?.some((b) => /WebView2/i.test(b.brand)) ? 'webview2-brand' : null;

// A2) UA'da gömülü/headless/otomasyon imzaları.
const sigBotUA = (ua: string): string | null => {
  if (/WebView2|Electron|HeadlessChrome|PhantomJS|SlimerJS|puppeteer|playwright|selenium/i.test(ua)) {
    return 'bot-ua';
  }
  // JS çalıştıran ama HTTP-client/scraper UA'sıyla gelen araçlar (bizim sistemde
  // zaten vardı — koruyoruz). Bu token'lar gerçek tarayıcı UA'sında bulunmaz.
  if (/\b(curl|wget|python-requests|python-urllib|Go-http-client|node-fetch|axios|okhttp|libwww-perl|Scrapy|Java)\b/i.test(ua)) {
    return 'http-client-ua';
  }
  return null;
};

// A3) Otomasyon bayrağı — Selenium/Puppeteer/Playwright set eder. İnsanın
//     sürdüğü gerçek tarayıcıda asla true olmaz.
const sigWebdriver = (nav: Navigator): string | null =>
  nav.webdriver === true ? 'webdriver-flag' : null;

// A4) Bilinen otomasyon global'leri.
const sigAutomationGlobals = (w: Record<string, any>): string | null => {
  const keys = [
    'callPhantom', '_phantom', '__nightmare',
    '__selenium_unwrapped', '__webdriver_evaluate', '__driver_evaluate',
    'domAutomation', 'domAutomationController',
  ];
  return keys.some((k) => k in w) ? 'automation-global' : null;
};

const groupA = (ua: string, w: Record<string, any>, nav: Navigator, uaData: NavigatorUAData | undefined): string | null =>
  sigEmbedded(ua, w) ||
  sigWebView2Brand(uaData) ||
  sigBotUA(ua) ||
  sigWebdriver(nav) ||
  sigAutomationGlobals(w);

// ===========================================================================
// B) TUTARSIZLIK (UA spoof) — tek sinyal yeter
// ===========================================================================

// B5) UA "Chrome" diyor ama userAgentData YOK → gerçek Chromium'da olmalı.
//     ÖNEMLİ GÜVENLİK: yalnızca güvenli bağlamda (HTTPS/localhost) geçerli.
//     HTTP (secure olmayan) bağlamda gerçek Chrome da userAgentData YAYINLAMAZ;
//     gate olmadan TÜM Chrome kullanıcıları bloklanırdı. Safari/Firefox'ta UA'da
//     "Chrome" geçmediği için zaten tetiklenmez (onlar meşru).
const sigChromeWithoutUAData = (ua: string, uaData: NavigatorUAData | undefined): string | null => {
  if (window.isSecureContext && /Chrome/i.test(ua) && !uaData) return 'chrome-ua-without-uadata';
  return null;
};

// B6) brands(Chromium) sürümü ↔ UA Chrome sürümü uyuşmazlığı (curl'deki durum:
//     UA 131, client-hints 149). Yalnızca "Chromium" markasına bakarız
//     (Opera/Brave kendi sürümünü raporlar → yanlış pozitife düşmeyelim).
const sigVersionMismatch = (ua: string, uaData: NavigatorUAData | undefined): string | null => {
  const chromiumBrand = uaData?.brands?.find((b) => b.brand === 'Chromium');
  const uaMajor = chromiumMajorFromUA(ua);
  if (chromiumBrand && uaMajor) {
    const brandMajor = parseInt(chromiumBrand.version, 10);
    if (brandMajor && brandMajor !== uaMajor) return 'ua-clienthints-version-mismatch';
  }
  return null;
};

// B7) userAgentData.platform ↔ UA işletim sistemi çelişkisi. Yalnızca emin
//     olduğumuz üç platformu kontrol ederiz (yanlış pozitiften kaçınmak için
//     Linux/ChromeOS/iOS atlanır).
const sigPlatformMismatch = (ua: string, uaData: NavigatorUAData | undefined): string | null => {
  const platform = uaData?.platform;
  if (!platform) return null;
  const expected: Record<string, RegExp> = {
    Windows: /Windows/i,
    macOS: /Macintosh|Mac OS X/i,
    Android: /Android/i,
  };
  const re = expected[platform];
  if (re && !re.test(ua)) return 'platform-ua-mismatch';
  return null;
};

// B8) UA "Chrome" İÇERMİYOR (Safari/Firefox iddiası) ama userAgentData dolu →
//     tutarsızlık = UA spoof'lanmış olabilir.
//     ÖNEMLİ: Bu sinyal SADECE MASAÜSTÜNDE çalışır. Test sonucu: masaüstü Safari
//     navigator.userAgentData DÖNDÜRMÜYOR (sorunsuz), ama mobil/iOS Safari DOLU
//     döndürüyor → mobilde bu sinyal gerçek Safari kullanıcısını bloklar. O yüzden
//     mobilde devre dışı; masaüstündeki spoof koruması korunur.
const sigNonChromeWithUAData = (ua: string, uaData: NavigatorUAData | undefined): string | null => {
  if (isMobileClient(ua, uaData)) return null; // mobilde yanlış pozitif → atla
  if (!/Chrome/i.test(ua) && (uaData?.brands?.length ?? 0) > 0) return 'non-chrome-with-uadata';
  return null;
};

const groupB = (ua: string, uaData: NavigatorUAData | undefined): string | null =>
  sigChromeWithoutUAData(ua, uaData) ||
  sigVersionMismatch(ua, uaData) ||
  sigPlatformMismatch(ua, uaData) ||
  sigNonChromeWithUAData(ua, uaData);

// ===========================================================================
// C) ZAYIF SİNYALLER — tek başına bloklamaz, EN AZ İKİSİ birleşince bloklar
// ===========================================================================

const groupCReasons = (ua: string, nav: Navigator, uaData: NavigatorUAData | undefined): string[] => {
  const reasons: string[] = [];
  const mobile = isMobileClient(ua, uaData);

  // C9) plugins.length === 0 + Chromium UA. Mobilde eklenti olmaması NORMAL →
  //     mobilde bu sinyali uygulama.
  const plugins = (nav as any).plugins;
  if (!mobile && chromiumMajorFromUA(ua) && plugins && plugins.length === 0) {
    reasons.push('no-plugins');
  }

  // C10) navigator.languages boş/tanımsız.
  if (!nav.languages || nav.languages.length === 0) reasons.push('empty-languages');

  // C11) pencere dış boyutu 0 (eski headless izi).
  if (window.outerWidth === 0 || window.outerHeight === 0) reasons.push('zero-outer-size');

  // C12) ekran boyutu 0.
  if (window.screen && (window.screen.width === 0 || window.screen.height === 0)) {
    reasons.push('zero-screen-size');
  }

  return reasons;
};

// ===========================================================================
// Karar + tek giriş noktası
// ===========================================================================

/**
 * A veya B grubundan herhangi bir sinyal tek başına bloklar; C grubundan en az
 * iki sinyal birleşince bloklar. Emin olunmayan durumda geçirir.
 */
export const detectBlockedClient = (): GuardResult => {
  // SSR / test ortamı güvencesi
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { blocked: false, reason: null };
  }

  const nav = navigator;
  const ua = nav.userAgent || '';
  const w = window as unknown as Record<string, any>;
  const uaData = getUAData(nav);

  const a = groupA(ua, w, nav, uaData);
  if (a) return { blocked: true, reason: a };

  const b = groupB(ua, uaData);
  if (b) return { blocked: true, reason: b };

  const weak = groupCReasons(ua, nav, uaData);
  if (weak.length >= 2) return { blocked: true, reason: `weak:${weak.join('+')}` };

  return { blocked: false, reason: null };
};

/** Spec uyumlu boolean kısayol. */
export const isSuspiciousClient = (): boolean => detectBlockedClient().blocked;

// --- Geriye dönük uyumluluk (mevcut çağrılar kırılmasın) ---------------------
export const detectEmbeddedRuntime = detectBlockedClient;
export const isBlockedClient = isSuspiciousClient;
export const isEmbeddedRuntime = isSuspiciousClient;
